import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../../context/AuthContext';
import {
  epcProjectService,
  type EpcProject,
  type EpcProjectMembershipRow,
} from '../../../services/epcProjectService';
import { isGovernanceRole } from './team/teamCapabilities';

/** Personal capability for the current user on this project. */
export type ProjectMyCap = 'governance' | 'dept_head' | 'manager' | 'engineer' | 'outsider';

/** Route param must be a 24-char hex Mongo ObjectId (API rejects anything else). */
export function parseRouteObjectId(raw: string | undefined): string | undefined {
  if (!raw || raw === 'undefined') return undefined;
  return /^[a-f0-9]{24}$/i.test(raw) ? raw : undefined;
}

export type EpcProjectDetailContextValue = {
  id: string;
  project: EpcProject | null;
  snapshot: Record<string, unknown> | null;
  importHistory: unknown[];
  loading: boolean;
  reload: () => Promise<void>;
  canImport: boolean;
  canUpdate: boolean;
  hasSchedule: boolean;
  /** All active project memberships (loaded after `project` resolves). */
  memberships: EpcProjectMembershipRow[];
  membershipsLoading: boolean;
  /** Current user's id (string), if logged-in. */
  myUserId: string | undefined;
  /** Current user's membership in this project (if any). */
  mine: EpcProjectMembershipRow | undefined;
  /** Personal capability — drives default route + tab visibility. */
  myCap: ProjectMyCap;
  reloadMemberships: () => Promise<void>;
};

const EpcProjectDetailContext = createContext<EpcProjectDetailContextValue | null>(null);

export function EpcProjectDetailProvider({ children }: { children: ReactNode }) {
  const { id: rawId } = useParams<{ id: string }>();
  const id = parseRouteObjectId(rawId) ?? '';
  const { user: authUser, hasPermission } = useAuth();
  const [project, setProject] = useState<EpcProject | null>(null);
  const [snapshot, setSnapshot] = useState<Record<string, unknown> | null>(null);
  const [importHistory, setImportHistory] = useState<unknown[]>([]);
  const [loading, setLoading] = useState(true);
  const [memberships, setMemberships] = useState<EpcProjectMembershipRow[]>([]);
  const [membershipsLoading, setMembershipsLoading] = useState(false);

  const canImport = hasPermission('epcProject', 'import');
  const canUpdate = hasPermission('epcProject', 'update');
  const isSuperAdmin = Boolean((authUser as { isSuperAdmin?: boolean } | null)?.isSuperAdmin);
  const myUserId = useMemo(() => {
    const u = authUser as { _id?: string; id?: string } | null;
    return u?._id ? String(u._id) : u?.id ? String(u.id) : undefined;
  }, [authUser]);

  const load = useCallback(async () => {
    if (!id) {
      setProject(null);
      setSnapshot(null);
      setImportHistory([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const p = await epcProjectService.get(id);
      setProject(p);
      const sch = await epcProjectService.getSchedule(id);
      setSnapshot(sch.snapshot);
      setImportHistory(sch.importHistory || []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load project');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadMemberships = useCallback(async () => {
    if (!id) {
      setMemberships([]);
      return;
    }
    try {
      setMembershipsLoading(true);
      const rows = await epcProjectService.listMemberships(id);
      setMemberships(rows);
    } catch (e) {
      console.error(e);
      /* Silent failure here: many routes don't depend on memberships. */
    } finally {
      setMembershipsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void loadMemberships();
  }, [loadMemberships]);

  const tasks = (snapshot?.tasks as Array<Record<string, unknown>> | undefined) || [];
  const hasSchedule =
    (project?.scheduleStats?.taskCount != null && project.scheduleStats.taskCount > 0) || tasks.length > 0;

  // All active memberships for this user in this project.
  const myActiveMemberships = useMemo<EpcProjectMembershipRow[]>(() => {
    if (!myUserId) return [];
    return memberships.filter((m) => m.userId === myUserId && !m.inactive);
  }, [memberships, myUserId]);

  // Determine effective capability by scanning ALL roles — `manager` beats
  // `department_head` so that stale/dual-role data (user has both memberships)
  // never silently shows the wrong workspace view.
  const myCap = useMemo<ProjectMyCap>(() => {
    if (isSuperAdmin) return 'governance';
    if (myActiveMemberships.length === 0) return 'outsider';
    const roles = new Set(myActiveMemberships.map((m) => m.role));
    if (['epc_head', 'pmo', 'project_head', 'project_coordinator'].some((r) => roles.has(r as EpcProjectMembershipRow['role']))) return 'governance';
    // manager beats department_head: a user with both memberships is treated as DM,
    // not DH. Keeps the wrong-workspace bug from surfacing on data anomalies.
    if (roles.has('manager')) return 'manager';
    if (roles.has('department_head')) return 'dept_head';
    if (roles.has('site_engineer')) return 'engineer';
    return 'outsider';
  }, [isSuperAdmin, myActiveMemberships]);

  // Return the membership whose role aligns with myCap so mine.role is coherent.
  const mine = useMemo<EpcProjectMembershipRow | undefined>(() => {
    if (myActiveMemberships.length === 0) return undefined;
    if (myCap === 'governance') {
      return (
        myActiveMemberships.find((m) => isGovernanceRole(m.role)) ??
        myActiveMemberships[0]
      );
    }
    const targetRole =
      myCap === 'manager' ? 'manager'
      : myCap === 'dept_head' ? 'department_head'
      : myCap === 'engineer' ? 'site_engineer'
      : null;
    return (
      (targetRole ? myActiveMemberships.find((m) => m.role === targetRole) : undefined) ??
      myActiveMemberships[0]
    );
  }, [myActiveMemberships, myCap]);

  const value = useMemo<EpcProjectDetailContextValue>(
    () => ({
      id,
      project,
      snapshot,
      importHistory,
      loading,
      reload: load,
      canImport,
      canUpdate,
      hasSchedule,
      memberships,
      membershipsLoading,
      myUserId,
      mine,
      myCap,
      reloadMemberships: loadMemberships,
    }),
    [
      id,
      project,
      snapshot,
      importHistory,
      loading,
      load,
      canImport,
      canUpdate,
      hasSchedule,
      memberships,
      membershipsLoading,
      myUserId,
      mine,
      myCap,
      loadMemberships,
    ],
  );

  return <EpcProjectDetailContext.Provider value={value}>{children}</EpcProjectDetailContext.Provider>;
}

export function useEpcProjectDetail(): EpcProjectDetailContextValue {
  const ctx = useContext(EpcProjectDetailContext);
  if (!ctx) {
    throw new Error('useEpcProjectDetail must be used within EpcProjectDetailProvider');
  }
  return ctx;
}
